import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import mapDispatchPropsToActions from 'store/helpers/mapDispatchToActions';
import {
	getVehicleLengthMakes,
	getVehicleLengthModels,
	getVehicleLengthModelInfo
} from 'store/vehicles/actions';

import { H3, H4, Text, Paragraph } from 'components/core/Typography';
import { SelectBox } from 'components/core/SelectBox';
import { Spinner } from 'components/core/Spinner';

import { initialState, ApiMinYear } from 'config/vehicleLengthTool';
import { getYears, getMakes, getModels, getModelInfo, handleError } from './utils';
import './VehicleLengthTool.scss';

// TODO: Don't query service when first select option is chosen (Year, Make, Model text)

export const enhance = compose(
	connect(
		undefined, {
			getVehicleLengthMakes,
			getVehicleLengthModels,
			getVehicleLengthModelInfo
		},
		mapDispatchPropsToActions
	),
	withStateHandlers(({ ...initialState }), {
		handleStateOnChange: () => (newState) => newState // Merges in state, does not completely replace
	}),
	withHandlers({
		handleYear: (props) => (event) => {
			props.handleStateOnChange({ isLoading: true, hasError: false });
			const selectedValue = parseInt(event.target.value);
			return props.actions.getVehicleLengthMakes({ year: selectedValue })
			.then(handleError(props), handleError(props, true))
			.then(getMakes)
			.then((makes) => {
				props.handleStateOnChange({
					makes,
					models: [...initialState.models],
					isLoading: false,
					vehicleFeet: -1,
					vehicleInches: -1,
					storageMinLength: -1,
					vehicleCouldNotBeFound: false,
					selected: {
						...initialState.selected,
						year: selectedValue
					}
				});
			});
		},
		handleMake: (props) => (event) => {
			props.handleStateOnChange({ isLoading: true, hasError: false });
			const selectedValue = event.target.value;
			return props.actions.getVehicleLengthModels({ year: props.selected.year, make: selectedValue })
			.then(handleError(props), handleError(props, true))
			.then(getModels)
			.then((models) => {
				props.handleStateOnChange({
					models,
					isLoading: false,
					vehicleFeet: -1,
					vehicleInches: -1,
					vehicleCouldNotBeFound: false,
					selected: {
						...props.selected,
						make: selectedValue
					}
				});
			});
		},
		handleModel: (props) => (event) => {
			props.handleStateOnChange({ isLoading: true, hasError: false });
			const selectedValue = event.target.value;
			return props.actions.getVehicleLengthModelInfo({
				year: props.selected.year,
				make: props.selected.make,
				model: selectedValue,
				limit: 1 // Only return one trim item from service, otherwise request will be massive
			})
			.then(handleError(props), handleError(props, true))
			.then(getModelInfo)
			.then((modelInfo) => {
				const vehicleFeet = get(modelInfo, 'modelLength.feet', -1);
				const vehicleInches = get(modelInfo, 'modelLength.inches', -1);
				const vehicleCouldNotBeFound = vehicleFeet <= 0;
				props.handleStateOnChange({
					vehicleFeet,
					vehicleInches,
					isLoading: false,
					storageMinLength: vehicleFeet + 2 + (vehicleInches > 0 ? 1 : 0),
					vehicleCouldNotBeFound,
					selected: {
						...props.selected,
						model: selectedValue
					}
				});
			});
		}
	}),
	lifecycle({
		componentDidMount() {
			this.props.handleStateOnChange({
				years: getYears(ApiMinYear, (new Date()).getFullYear()),
				isLoading: false
			});
		}
	})
);

export function VehicleLengthTool({
	isLoading,
	hasError,
	years,
	makes,
	models,
	selected,
	handleYear,
	handleMake,
	handleModel,
	vehicleFeet,
	vehicleInches,
	storageMinLength,
	vehicleCouldNotBeFound
}) {
	return (
		<div className="vehicle-length-tool">
			<Spinner show={isLoading} />
			<div className="row">
				<H3 className="title" size="medium">
					Not sure what size unit you need to store your vehicle?
				</H3>
				<Paragraph size="small" weight="medium">
					Enter your vehicle's information and we will find the ideal unit size for you.
				</Paragraph>
				<div className="dropdown-row">
					<SelectBox
						label="Year"
						id="vehicle-year"
						options={years}
						value={selected.year}
						onChange={handleYear}
					/>
					<SelectBox
						label="Make"
						id="vehicle-make"
						options={makes}
						value={selected.make}
						onChange={handleMake}
					/>
					<SelectBox
						label="Model"
						id="vehicle-model"
						options={models}
						value={selected.model}
						onChange={handleModel}
					/>
				</div>
				{vehicleFeet > 0 &&
					<Paragraph className="bottom-text" size="small" weight="medium">
						The ideal storage size is a couple feet longer than your vehicle.
					</Paragraph>
				}
				{hasError &&
					<Paragraph className="bottom-text" size="small" weight="medium">
						Oops, looks like something went wrong on our end. Try selecting your vehicle again.
					</Paragraph>
				}
			</div>
			{vehicleFeet > 0 &&
				<div className="row footer-row dark">
					<div className="block">
						<H4 size="medium">
							Length of your vehicle:
						</H4>
						<Text size="huge">
							{vehicleFeet}{vehicleInches > 0 ? `'${vehicleInches}"` : ' feet'}
						</Text>
					</div>
					<div className="block">
						<H4 size="medium">
							Minimum unit length recommended:
						</H4>
						<Text size="huge">
							{storageMinLength}+ feet
						</Text>
					</div>
				</div>
			}
			{vehicleCouldNotBeFound &&
				<div>
					<div className="row dark">
						<H3 size="medium">
							Your ride must be a real treasure you're going to have to measure!
						</H3>
						<Paragraph size="small" weight="medium">
							We are unable to provide length data for your car. See our handy guide below.
						</Paragraph>
					</div>
					<div className="row footer-row">
						<H4 size="small">
							Approximate storage unit lengths for cars:
						</H4>
						<div className="block">
							<Paragraph size="small" weight="medium">
								8' Long = Motorcycles
							</Paragraph>
							<Paragraph size="small" weight="medium">
								20' = Most cars, small trucks
							</Paragraph>
						</div>
						<div className="block">
							<Paragraph size="small" weight="medium">
								15' Long = Compact cars
							</Paragraph>
							<Paragraph size="small" weight="medium">
								30' = Multiple cars, larger trucks and vans
							</Paragraph>
						</div>
					</div>
				</div>
			}
		</div>
	);
}

VehicleLengthTool.propTypes = {
	years: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.number.isRequired,
		display: PropTypes.string.isRequired
	})),
	makes: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.string.isRequired,
		display: PropTypes.string.isRequired
	})),
	models: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.string.isRequired,
		display: PropTypes.string.isRequired
	})),
	selected: PropTypes.shape({
		year: PropTypes.number.isRequired,
		make: PropTypes.string.isRequired,
		model: PropTypes.string.isRequired
	}),
	handleYear: PropTypes.func.isRequired,
	handleMake: PropTypes.func.isRequired,
	handleModel: PropTypes.func.isRequired,
	vehicleFeet: PropTypes.number.isRequired,
	vehicleInches: PropTypes.number.isRequired,
	storageMinLength: PropTypes.number.isRequired,
	vehicleCouldNotBeFound: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool.isRequired,
	hasError: PropTypes.bool.isRequired
};

export default enhance(VehicleLengthTool);
